import type { CSSProperties, ReactNode, Ref } from "react";
import { fontFamilyName, lineBox, textBlockMargins, textBlockMetrics } from "./fonts";
import { borderOverlays, fills, type BoxNode, type BoxStyle, type Node, type Overlay, type PageSpec, type TextNode } from "./ir";
import { flowWidths, type PagedDoc, type PageFrame } from "./paginate";
import { cssLength } from "./units";

// Renders a template's layout tree as DOM at real size, in points: the flows
// the paginator measures (FlowView) and the finished pages (ResumePage).
// renderPdf.tsx draws the same page fragments with react-pdf.

export const PT_TO_PX = 96 / 72;

/** Every length goes through the shared layout grid (units.ts). */
const pt = cssLength;

const ALIGN: Record<NonNullable<BoxStyle["align"]>, CSSProperties["alignItems"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
};

const JUSTIFY: Record<NonNullable<BoxStyle["justify"]>, CSSProperties["justifyContent"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
};

type Parent = { dir: "row" | "column"; align: NonNullable<BoxStyle["align"]> };

const FLOW_PARENT: Parent = { dir: "column", align: "start" };

/** How a child sits in its parent: flex sizing plus stretching full-width children. */
function itemStyle(n: Node, parent: Parent): CSSProperties {
  const grow = n.kind === "text" ? n.grow : n.kind === "box" ? n.style.grow : undefined;
  const shrinkable = n.kind === "text" ? n.shrinkable : n.kind === "box" ? n.style.shrinkable : false;
  const s: CSSProperties = {};
  if (parent.dir === "row" && grow) {
    s.flex = `${grow} 1 0`;
    s.minWidth = 0;
  } else if (parent.dir === "row" && shrinkable) {
    s.flex = "0 1 auto";
    s.minWidth = 0;
  } else {
    s.flex = "0 0 auto";
  }
  if (parent.dir === "column" && parent.align !== "stretch" && fills(n)) s.alignSelf = "stretch";
  return s;
}

function boxStyle(b: BoxNode): CSSProperties {
  const s = b.style;
  const dir = s.dir ?? "column";
  const css: CSSProperties = {
    display: "flex",
    flexDirection: dir,
    alignItems: ALIGN[s.align ?? (dir === "row" ? "center" : "start")],
    boxSizing: "border-box",
  };
  if (s.wrap) css.flexWrap = "wrap";
  if (s.gap || s.runGap) {
    css.columnGap = pt(dir === "row" ? s.gap ?? 0 : s.runGap ?? 0);
    css.rowGap = pt(dir === "row" ? s.runGap ?? 0 : s.gap ?? 0);
  }
  if (s.justify) css.justifyContent = JUSTIFY[s.justify];
  if (s.alignSelf) css.alignSelf = ALIGN[s.alignSelf];
  if (s.width !== undefined) css.width = s.width === "100%" ? "100%" : pt(s.width);
  if (s.height !== undefined) css.height = pt(s.height);
  if (s.pad) css.padding = s.pad.map(pt).join(" ");
  if (s.margin) css.margin = s.margin.map(pt).join(" ");
  if (s.bg) css.background = s.bg;
  if (s.radius) css.borderRadius = pt(s.radius);
  if (s.position) css.position = s.position;
  else if (s.border || s.borderLeft || s.borderBottom) css.position = "relative";
  if (s.top !== undefined) css.top = pt(s.top);
  if (s.left !== undefined) css.left = pt(s.left);
  if (s.right !== undefined) css.right = pt(s.right);
  if (s.bottom !== undefined) css.bottom = pt(s.bottom);
  return css;
}

function renderOverlay(o: Overlay, key: string): ReactNode {
  const css: CSSProperties = { position: "absolute", boxSizing: "border-box", pointerEvents: "none" };
  if (o.top !== undefined) css.top = pt(o.top);
  if (o.right !== undefined) css.right = pt(o.right);
  if (o.bottom !== undefined) css.bottom = pt(o.bottom);
  if (o.left !== undefined) css.left = pt(o.left);
  if (o.width !== undefined) css.width = pt(o.width);
  if (o.height !== undefined) css.height = pt(o.height);
  if (o.bg) css.background = o.bg;
  if (o.radius) css.borderRadius = pt(o.radius);
  if (o.stroke) {
    const line = `${pt(o.stroke.width)} ${o.stroke.dashed ? "dashed" : "solid"} ${o.stroke.color}`;
    if (o.stroke.sides === "all") css.border = line;
    else css.borderTop = line;
  }
  return <span key={key} data-ov="" aria-hidden="true" style={css} />;
}

/** U+2060 WORD JOINER: invisible, forbids a line break on either side. */
export const WJ = "⁠";

/**
 * Like the app's renderer, never break after a hyphen ("cross-functional"
 * stays whole): a word joiner after each in-word hyphen. Unlike `nowrap`, a
 * word longer than the column can still break (overflow-wrap), so a long
 * hyphenated URL wraps instead of running off the page. Line-break offsets
 * skip the joiners (lineBreaks.ts).
 */
function joinHyphens(value: string): string {
  return value.includes("-") ? value.replace(/-(?=\S)/g, `-${WJ}`) : value;
}

/**
 * A text block. Its line boxes are pinned to the shared metrics
 * (textBlockMetrics): the block's own font and line-height form the strut,
 * and the runs get `line-height: 0` so their inline boxes — built from
 * pixel-rounded platform metrics — can never make a line taller. The margins
 * then place the first baseline at the tallest run ascent below the block's
 * top and make the block take lines × pitch − lineSpacing, exactly like the
 * PDF (where the extra leading of the last line is pulled back instead).
 */
function renderText(n: TextNode, key: number, parent: Parent): ReactNode {
  const first = n.runs[0];
  const ls = n.lineSpacing;
  const css: CSSProperties = {
    ...itemStyle(n, parent),
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    textAlign: n.align,
  };
  if (first) {
    const [top, bottom] = textBlockMargins(n.runs, ls);
    css.margin = `${pt(top)} 0 ${pt(bottom)}`;
    css.fontFamily = `'${fontFamilyName(first.font)}', sans-serif`;
    css.fontSize = pt(first.size);
    css.lineHeight = pt(textBlockMetrics(n.runs, ls).lineHeight);
  }
  return (
    <div key={key} style={css} data-rt="" data-section={n.tag}>
      {n.runs.map((r, i) => (
        <span
          key={i}
          style={{
            fontFamily: `'${fontFamilyName(r.font)}', sans-serif`,
            fontSize: pt(r.size),
            lineHeight: 0,
            color: r.color,
            letterSpacing: r.letterSpacing ? pt(r.letterSpacing) : undefined,
          }}
        >
          {joinHyphens(r.text)}
        </span>
      ))}
    </div>
  );
}

export function renderNode(n: Node, key: number, parent: Parent): ReactNode {
  if (n.kind === "text") return renderText(n, key, parent);
  if (n.kind === "glyph") {
    return (
      <svg
        key={key}
        viewBox="0 0 24 24"
        width={pt(n.size)}
        height={pt(n.size)}
        style={{ ...itemStyle(n, parent), display: "block" }}
        aria-hidden="true"
      >
        <path d={n.path} fill={n.color} />
      </svg>
    );
  }
  if (n.kind === "image") {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- a data URL photo inside a print layout, not a page image
      <img
        key={key}
        src={n.src}
        alt=""
        draggable={false}
        style={{
          ...itemStyle(n, parent),
          display: "block",
          width: pt(n.size),
          height: pt(n.size),
          borderRadius: n.radius ? pt(n.radius) : undefined,
          objectFit: "cover",
        }}
      />
    );
  }
  const dir = n.style.dir ?? "column";
  const self: Parent = { dir, align: n.style.align ?? (dir === "row" ? "center" : "start") };
  return (
    <div key={key} style={{ ...itemStyle(n, parent), ...boxStyle(n) }} data-section={n.tag}>
      {borderOverlays(n.style).map((o, i) => renderOverlay(o, `b${i}`))}
      {n.children.map((child, i) => renderNode(child, i, self))}
    </div>
  );
}

const flowStyle = (width: number): CSSProperties => ({
  width: pt(width),
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  boxSizing: "border-box",
});

/** One flowing column of blocks at [width] (what the paginator measures). */
export function FlowView({ nodes, width, innerRef }: { nodes: Node[]; width: number; innerRef?: Ref<HTMLDivElement> }) {
  return (
    <div ref={innerRef} style={flowStyle(width)}>
      {nodes.map((n, i) => renderNode(n, i, FLOW_PARENT))}
    </div>
  );
}

/** The footer of a multi-page document: "Name · 2 / 3". */
export function footerText(spec: PageSpec, index: number, count: number): string {
  return count > 1 ? [spec.name, `${index + 1} / ${count}`].filter(Boolean).join("  ·  ") : "";
}

/** One finished page: frame, bands, its share of the content, footer. */
export function ResumePage({ doc, index, frame }: { doc: PagedDoc; index: number; frame?: PageFrame }) {
  const spec = doc.spec;
  const page = frame ?? doc.pages[index];
  const [top, right, bottom, left] = spec.pad;
  const widths = flowWidths(spec);
  const footer = footerText(spec, index, doc.pages.length);
  return (
    <div
      data-resume-page={index}
      style={{
        position: "relative",
        zIndex: 0,
        width: pt(spec.size.width),
        height: pt(spec.size.height),
        boxSizing: "border-box",
        padding: [index === 0 ? (spec.firstTop ?? top) : top, right, bottom, left].map(pt).join(" "),
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        background: "#fff",
        color: "#000",
        overflow: "hidden",
        textAlign: "left",
        fontKerning: "normal",
      }}
    >
      {spec.bands.map((band, i) => (
        <div
          key={`band-${i}`}
          aria-hidden="true"
          style={{ position: "absolute", zIndex: -1, top: 0, bottom: 0, [band.side]: 0, width: pt(band.width), background: band.color }}
        />
      ))}
      {page.body ? (
        <FlowView nodes={page.body} width={widths.body} />
      ) : (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", flex: "0 0 auto" }}>
          <FlowView nodes={page.left ?? []} width={widths.left} />
          <FlowView nodes={page.right ?? []} width={widths.right} />
        </div>
      )}
      {footer ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            right: pt(spec.footer.right),
            bottom: pt(spec.footer.bottom),
            fontFamily: `'${fontFamilyName(spec.footer.font)}', sans-serif`,
            fontSize: pt(7),
            lineHeight: pt(lineBox(spec.footer.font, 7)),
            letterSpacing: pt(0.4),
            color: spec.footer.color,
            whiteSpace: "pre",
          }}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}
